'use client';

import { PropsWithChildren, useEffect, useRef } from 'react';
import { brazeService, BrazeUserProps } from 'lib/braze';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { usePathname } from 'next/navigation';

const API_KEY = process.env.NEXT_PUBLIC_BRAZE_API_KEY || '';
const BASE_URL = process.env.NEXT_PUBLIC_BRAZE_BASE_URL || '';
const ENABLE_LOGGING = (process.env.NODE_ENV || '') !== 'production';

const BRAZE_ENABLE_KEY = 'discovery-next.braze-enabled';
const BRAZE_IAM_ENABLE_KEY = 'discovery-next.braze-iam-enabled';

interface BrazeProviderProps extends PropsWithChildren {
  user?: BrazeUserProps;
}

export function BrazeProvider({ children, user }: BrazeProviderProps) {
  const initialized = useRef(false);
  const pathname = usePathname();
  const brazeEnabled = useFeatureIsOn(BRAZE_ENABLE_KEY);
  const brazeIamEnabled = useFeatureIsOn(BRAZE_IAM_ENABLE_KEY);

  useEffect(() => {
    if (!brazeEnabled || initialized.current) return;

    try {
      brazeService.init({
        apiKey: API_KEY,
        baseUrl: BASE_URL,
        enableLogging: ENABLE_LOGGING,
        enableIam: brazeIamEnabled
      });

      initialized.current = true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[BrazeProvider] `init`', error);
    }

    return () => {
      if (!initialized.current) return;
      brazeService.destroy();
      initialized.current = false;
    };
  }, [brazeEnabled, brazeIamEnabled]);

  useEffect(() => {
    if (!brazeEnabled || !initialized.current) return;

    try {
      brazeService.flushDeferredIam();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[BrazeProvider] `flushDeferredIam`', error);
    }
  }, [brazeEnabled, pathname]);

  useEffect(() => {
    if (!user || !brazeEnabled) return;

    const identify = async () => {
      try {
        await brazeService.identifyUser(user);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[BrazeProvider] `identifyUser`', error);
      }
    };
    identify();
  }, [user, brazeEnabled]);

  return <>{children}</>;
}
